import { useCallback, useMemo } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useBannerContext } from "oolib";
import { useOAuthPopupConnect } from "@/_self_managed/DirectDataImport/hooks/oauthPopup";

/**
 * react-query hooks for the externalConnections framework
 * (slack / fathom / zohoDesk / …).
 *
 * Two things are addressed here, and they are not the same thing:
 *   - the CONNECTION: one shared credential per provider per platform. Connect,
 *     disconnect, and the account/status fields hang off it.
 *   - its DATASETS: an integration may import into several, each with its own
 *     channel scope, schedule and cursor. Anything per-dataset takes a
 *     contentType.
 *
 * Cache key is ["externalConnection", provider] — exported so callers can
 * invalidate via the standard `useQueryClient().invalidateQueries(...)` from
 * react-query without recreating the shape locally. One key covers a provider
 * AND all of its datasets, so any per-dataset mutation invalidates the same
 * entry.
 */

export const externalConnectionKey = (provider) => ["externalConnection", provider];

// Error names the backend sets deliberately, having written those messages for a
// person to read. Everything else — a validation string aimed at developers, a
// database driver timeout, an unexpected throw — is NOT in this list.
//
// This exists because the backend's error handler puts the raw thrown message
// into the response (see serverUtils/handleErrorResponse). Showing that verbatim
// would put wording like "body.cronConfig is required", or a driver error naming
// internal hosts, straight into a banner. So we match on the name and fall back
// to the caller's own sentence whenever we don't recognise it.
const USER_FACING_ERROR_NAMES = [
  "SYNC_ALREADY_IN_PROGRESS",
  "MANUAL_SYNC_UNSUPPORTED",
  "EXT_CONN_NOT_FOUND",
  "EXT_CONN_NOT_ACTIVE",
  "INTEGRATION_LINK_NOT_FOUND",
];

/**
 * Pick the message to show for a failed request.
 *
 * Returns the backend's own wording only for the errors it marked as
 * user-facing; otherwise `fallback`. Also returns the error name so callers can
 * branch on it (e.g. styling "already syncing" as info rather than a failure).
 */
const readRequestError = (err, fallback) => {
  const name = err?.response?.data?.errors?.[0]?.name;
  const msg = err?.response?.data?.errors?.[0]?.msg;
  return {
    name,
    msg: USER_FACING_ERROR_NAMES.includes(name) && msg ? msg : fallback,
  };
};

/**
 * Read connection status for a provider, including every dataset it feeds.
 *
 * Response shape:
 *   { connected: false, links: [] }
 *   { connected: true, status, providerAccountName, adminEmail, lastUsedAt,
 *     authorizedBy?, connectedAt, supportsIncrementalSync,
 *     links: [{ _id, contentType, lastImport, cronConfig, syncInProgress,
 *               consecutiveFailureCount, autoPausedAt, autoPausedReason,
 *               createdAt }] }
 *
 * An empty `links` array on a connected provider is the "authenticated, setup
 * required" state — the credential is good but no dataset is wired up yet.
 */
export const useExternalConnectionStatus = (provider, options = {}) => {
  return useQuery(
    externalConnectionKey(provider),
    () => axios.get(`/api/externalConnections/${provider}/status`).then((r) => r.data),
    {
      enabled: !!provider,
      ...options,
    }
  );
};

/**
 * Kick off the OAuth flow in a popup (same mechanics as the Direct Data Import
 * connect flow — see hooks/oauthPopup.js). The provider auth runs in a popup so
 * the Integrations page stays mounted; on return we invalidate this provider's
 * status query so the card flips to connected. Falls back to a full-page
 * redirect only if the popup is blocked at open time.
 *
 * Returns `{ connect, isConnecting }` — call `connect()` from a click handler
 * (it must open the popup inside the user gesture) and use `isConnecting` to
 * drive the button's loading state.
 */
export const useConnectExternalConnection = (provider, { providerName, onConnected } = {}) => {
  const { SET_INFO_BANNER } = useBannerContext();
  const qc = useQueryClient();

  const { startConnect, isConnecting } = useOAuthPopupConnect({
    providerId: provider,
    providerName: providerName || provider,
    onResult: ({ result, reason }) => {
      if (result === "error") {
        SET_INFO_BANNER(
          `Connection failed: ${reason || "unknown"}. Please try again.`,
          "red",
          5000
        );
      }
      // Refetch status so the card reflects the new connection immediately,
      // then hand the fresh status to onConnected on success.
      qc.invalidateQueries(externalConnectionKey(provider)).then(() => {
        if (result === "success" && onConnected)
          onConnected(qc.getQueryData(externalConnectionKey(provider)));
      });
    },
  });

  const connect = useCallback(async () => {
    try {
      await startConnect();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Could not start connection";
      SET_INFO_BANNER(msg, "red", 5000);
    }
  }, [startConnect, SET_INFO_BANNER]);

  return { connect, isConnecting };
};

/**
 * Patch the cronConfig for ONE dataset of a provider. The okf-sub scheduler
 * (hourly / daily / weekly cron tiers) reads these settings to decide which
 * datasets to sync, so two datasets of the same integration can run on
 * different frequencies — or one on a schedule and one not at all.
 *
 * Pass any subset of { enabled, frequency, scope } to mutate that field —
 * the BE only $sets fields you send. `frequency` must be one of
 * "hourly" | "daily" | "weekly" (or omitted when enabled=false).
 *
 * Setting enabled=true also clears any auto-pause marker on the BE and resets
 * the dataset's failure streak, so a dataset that repeated failures switched
 * off starts fresh when the user switches it back on.
 */
export const useUpdateCronConfig = (provider, contentType) => {
  const qc = useQueryClient();
  const { SET_INFO_BANNER } = useBannerContext();
  return useMutation(
    // A contentType passed with the call overrides the hook's, so one hook can
    // serve every dataset in a list.
    ({ contentType: ct = contentType, ...cronConfig }) =>
      axios.patch(
        `/api/externalConnections/${provider}/datasets/${ct}/cronConfig`,
        { cronConfig }
      ),
    {
      onSuccess: () => {
        qc.invalidateQueries(externalConnectionKey(provider));
      },
      onError: (err) => {
        const { msg } = readRequestError(err, "Could not update the sync schedule.");
        SET_INFO_BANNER(msg, "red", 5000);
      },
    }
  );
};

/**
 * Trigger an immediate incremental sync ("Sync new data") for ONE dataset. Runs
 * the same fetch-only-new-data pipeline that dataset's schedule uses, on demand
 * — no channel/date picker. Independent of whether a schedule is enabled, and
 * independent of the integration's other datasets: a sibling already syncing
 * does not block this one.
 *
 * On success we invalidate the status query so the "Last sync" row refetches.
 * The BE either fans out an import ({ importId, totalRecords, ... }) or reports
 * a quiet no-op ({ noOp: true }) when nothing new was found.
 *
 * On error we show the backend's wording only for the errors it marked as
 * user-facing (see readRequestError), and our own sentence otherwise.
 * SYNC_ALREADY_IN_PROGRESS is styled as info rather than a failure, since a
 * running sync (cron or another click) will pull the new data and no retry is
 * needed; everything else is a red error banner.
 */
export const useSyncNowExternalConnection = (provider, { providerName, contentType } = {}) => {
  const qc = useQueryClient();
  const { SET_INFO_BANNER } = useBannerContext();
  return useMutation(
    // Same per-call contentType override as useUpdateCronConfig.
    ({ contentType: ct = contentType } = {}) =>
      axios
        .post(`/api/externalConnections/${provider}/datasets/${ct}/syncNow`)
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        qc.invalidateQueries(externalConnectionKey(provider));
        const label = providerName || provider;
        if (data?.noOp) {
          SET_INFO_BANNER(`${label} is up to date — no new data.`, "green", 4000);
        } else {
          SET_INFO_BANNER(`${label} sync started and your data will be pulled in shortly.`, "green", 4000);
        }
      },
      onError: (err) => {
        const { name, msg } = readRequestError(err, "Sync failed. Please try again.");
        // "Already in progress" isn't a real failure — a running sync (cron or
        // another click) will pull the new data — so show it as an info heads-up
        // (yellow), not a red error.
        if (name === "SYNC_ALREADY_IN_PROGRESS") {
          SET_INFO_BANNER(msg, "yellow", 5000);
        } else {
          SET_INFO_BANNER(msg, "red", 5000);
        }
      },
    }
  );
};

/**
 * Disconnect a provider. Removes the shared credential AND every dataset link
 * hanging off it, so all of that integration's schedules stop. The datasets
 * themselves and their documents survive — only the integration's memory of
 * them goes. Invalidates the status query on success so any card / modal
 * reading that key refetches.
 */
export const useDisconnectExternalConnection = (provider, { providerName } = {}) => {
  const qc = useQueryClient();
  const { SET_INFO_BANNER } = useBannerContext();
  return useMutation(
    () => axios.post(`/api/externalConnections/${provider}/disconnect`),
    {
      onSuccess: () => {
        qc.invalidateQueries(externalConnectionKey(provider));
        SET_INFO_BANNER(`${providerName || provider} disconnected.`, "green", 4000);
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || err?.message || "Disconnect failed";
        SET_INFO_BANNER(msg, "red", 5000);
      },
    }
  );
};

/**
 * contentType → provider, for every dataset any integration currently imports
 * into. One entry per dataset link, so an integration feeding three datasets
 * contributes three entries.
 *
 * Consumers: the Datasets listing's "Linked Integration" column and the
 * Topics tab's dataset badges (which show the provider's icon instead of the
 * dataset's initials). Lives here, next to the status hooks it composes,
 * because it has more than one consumer.
 *
 * Why explicit per-provider hook calls instead of a config-driven loop:
 *   - hooks can't be called from a callback, so a loop would break the rules of
 *     hooks even over a fixed-length list;
 *   - adding a new provider takes 2 grep-able lines here (hook call + entry in
 *     the array below);
 *   - forgetting surfaces as a missing link for that provider in QA, which is a
 *     loud, immediate signal;
 *   - react-query dedupes by queryKey, so each call shares its cache with the
 *     Integrations tab's useExternalConnectionStatus(provider) — no duplicate
 *     network requests.
 */
export const useLinkedIntegrationByContentType = () => {
  const slack     = useExternalConnectionStatus("slack").data?.links;
  const fathom    = useExternalConnectionStatus("fathom").data?.links;
  const typeform  = useExternalConnectionStatus("typeform").data?.links;
  const airtable  = useExternalConnectionStatus("airtable").data?.links;
  const discord   = useExternalConnectionStatus("discord").data?.links;
  const intercom  = useExternalConnectionStatus("intercom").data?.links;
  const notion    = useExternalConnectionStatus("notion").data?.links;
  // apiToken provider — a pasted token instead of OAuth, but it keeps the same
  // connection row and dataset links, so it surfaces here like the rest.
  const kobo      = useExternalConnectionStatus("kobo").data?.links;
  // platformManaged review providers — they keep a connection record (no token)
  // so they surface here just like OAuth ones.
  const playStore = useExternalConnectionStatus("playStoreReviews").data?.links;
  const appStore  = useExternalConnectionStatus("appStoreReviews").data?.links;

  const byProvider = [
    ["slack", slack],
    ["fathom", fathom],
    ["typeform", typeform],
    ["airtable", airtable],
    ["discord", discord],
    ["intercom", intercom],
    ["notion", notion],
    ["kobo", kobo],
    ["playStoreReviews", playStore],
    ["appStoreReviews", appStore],
  ];

  // Serialise the (provider, contentType) pairs so the memo only recomputes
  // when a dataset is actually added, removed or re-pointed — not on every
  // unrelated status refetch (lastImport timestamps change constantly).
  const signature = byProvider
    .map(([provider, links]) => `${provider}:${(links || []).map((l) => l.contentType).join(",")}`)
    .join("|");

  return useMemo(() => {
    const map = {};
    byProvider.forEach(([provider, links]) => {
      (links || []).forEach((link) => {
        if (link.contentType) map[link.contentType] = provider;
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};
